export type RequirementType = "Leasing" | "Hybrid" | "Smart EPP";
export type customerType = "customer" | "prospects" ;

export type LeasingProduct = "Asset Leasing" | "Sale & Leaseback";
export type HybridProduct =
  | "Working Capital"
  | "Term Loan"
  | "Line of Credit"
  | "Trade Finance"
  | "Overdraft"
  | "Bill Discounting"
  | "Project Finance"
  | "Supply Chain Financing"
  | "Loan Syndication"
  | "Factoring"
  | "Bank Guarantee"
  | "Venture Debt";

export type Product = number;

export interface Customer {
  id: string;
  name: string;
  logo?: string;
  brandName?: string;
}

export interface FilterState {
  search: string;
  products: Product[];
  statuses: string[];
  applicationRMs: string[];
  dateRange: { from?: Date; to?: Date };
  lenderInstitutions: string[];
  lenderNames: string[];
  requirementTypes: RequirementType[];
  customerType: customerType[];
  showStagnant: boolean;
  currentPageFilter: "loggedIn" | "toBeLoggedIn" | "droppedCases";
}

export interface Requirement {
  id: string;
  type: any;
  quantum: number;
  tenure: number; // months
  applicationRM: string;
  dealNote?: string;
  createdAt: string; // ISO
  createdBy: string;
  lenders: LenderTag[];
  customerId?:string;
  requirementId?:string;
  customerName?:string;
  customerBrandName?:string;
  customerLogo?:string;
  product_type_id?:number;
  asset_category_id?: number;
  productTypeName?:string;
  status?:number;
  products: Product[];
  customer?: Customer; // Optional for backwards compatibility
  disbursements?: DisbursementTranche[]; // Multiple tranches for disbursement tracking
  fulfilled?: boolean;
  applicationRMId?:string;
  assetCategoryName?:string;
  disbursedAmount?:number;
}

// Disbursement status for tracking
export type DisbursementStatus = "Active" | "Matured" | "Overdue" | "Rental Paid" | "Outstanding";

// Rental frequency
export type RentalFrequency = "Monthly" | "Quarterly";

// Funding mode
export type FundingMode = "A" | "B" | "C";

export interface DisbursementTranche {
  id: string;
  lenderId: string; // Links to LenderTag
  disbursedOn: string; // ISO date
  disbursedAmount: number;
  rentalScheduleNumber: string; // e.g., "RS-2026-001"
  lenderPF: number; // Processing Fee
  dueDate: string; // ISO date
  // EMI/Rental structure fields
  monthlyRental: number;
  numberOfRentals: number;
  interestRate: number; // percentage
  // New extended fields for Leasing
  commencementDate?: string; // ISO date
  maturedOn?: string; // ISO date
  tenure?: number; // Total tenure in months
  paidCount?: number; // Number of rentals paid
  remainingCount?: number; // Remaining rentals
  rentalFrequency?: RentalFrequency; // Monthly or Quarterly
  fundingMode?: FundingMode; // Pre GST or Post GST
  noaNachActive?: boolean; // NACH mandate active
  status?: DisbursementStatus; // Current status
  dueAmount?: number; // Due amount for current period
  overdueDays?: number; // Days overdue (if any)
  createdBy?: string;
  createdAt?: string; // ISO date
  // Payout details for Hybrid DSA mode
  partnerPayoutAmount?: number;
  customerPayoutAmount?: number;
  lenderPayoutAmount?: number;
  payoutBasis?: "sanctioned" | "disbursed" | "manual";
  customerPayoutPercent?: number;
  lenderPayoutPercent?: number;
  partnerPayoutPercent?: number;
  last_to_date?:string;
}

export type LenderStatus =
  "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "Disbursed"
  | "Sanctioned"
  | "Deal Shared"
  | "Credit Discussion Completed"
  | "Under Review"
  | "Unresponsive"
  | "On Hold"
  | "Rejected"
  | "Term Sheet Shared"
  | "No Lenders";

export interface StatusHistoryEntry {
  status?: LenderStatus;
  timestamp: string; // ISO
  updatedBy: string;
  note?: string;
  reason?: string;
  termSheetUrl?: string;
  termSheetName?: string;
  sanctionAmount?: number;
  sanctionLetterUrl?: string;
  sanctionLetterName?: string;
  // Disbursed details
  disbursedAmount?: number;
  customerPayout?: number;
  lenderPayout?: number;
  partnerPayout?: number;
  totalRevenue?: number; // calculated
  // Optional: basis and percentage breakdowns used for calculations
  payoutBasis?: "sanctioned" | "disbursed" | "manual";
  customerPayoutPercent?: number;
  lenderPayoutPercent?: number;
  partnerPayoutPercent?: number;
  statusId?:number
  sanctionDate?:string;
  sanctionValidTill?:string;
  collateralDetails?:string;
}

export interface LenderTag {
  id?: string;
  requirementId:string;
  customerId:string | null;
  institutionId: string;
  institutionName: string;
  lenderId: string;
  lenderName: string;
  programLimit?: number;
  applicationRM?: string;
  note?: string;
  taggedAt: string; 
  statusHistory: StatusHistoryEntry[];
  termSheetName?:string;
  sanctionLetterName?:string;
  termSheetLink?:string;
  sanctionLetterLink?:string;
  lender?:string;
  institutionAvatar?:string;
  comments:Comment[]
  name: string;
}
export type CommentResolutionStatus = 'pending' | 'resolved';
export type CommentIcon = 'phone' | 'email' | null;
export interface Comment {
  id: string;
  text: string;
  author: string;
  date: string;
  mentions?: string[];
  icon?: CommentIcon;
  resolutionStatus?: CommentResolutionStatus;
}

export interface DealDeskState {
  requirements: Requirement[];
  filters?: FilterState;
}

export const LEASING_PRODUCTS: LeasingProduct[] = [
  "Asset Leasing",
  "Sale & Leaseback",
];

export const HYBRID_PRODUCTS: HybridProduct[] = [
  "Working Capital",
  "Term Loan",
  "Line of Credit",
  "Trade Finance",
  "Overdraft",
  "Bill Discounting",
  "Project Finance",
  "Supply Chain Financing",
  "Loan Syndication",
  "Factoring",
  "Bank Guarantee",
  "Venture Debt",
];

// Requirement-level status workflow
export type RequirementStatus = 
  | "new_application" 
  | "due_diligence" 
  | "unresponsive" 
  | "on_hold" 
  | "cc_approved" 
  | "cl_rejected" 
  | "cc_rejected";

export const REQUIREMENT_STATUSES: RequirementStatus[] = [
  "new_application",
  "due_diligence",
  "unresponsive",
  "on_hold",
  "cc_approved",
  "cl_rejected",
  "cc_rejected",
];

